<div class="fulfill-task-dialog">
  <!-- Only show dialog header if in dialog mode -->
  @if (dialogRef) {
  <div class="dialog-header g-just-flex flex-between gap-20">
    <div>
      <h2 mat-dialog-title>{{ getTaskTypeDisplay() }}</h2>
      <p class="subtitle-text-color-2 small-subtitle">{{ task.description || 'No description' }}</p>
    </div>
    <button mat-icon-button (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>
  } @else {
  <!-- Standalone mode header -->
  <mat-card class="mb-20">
    <mat-card-header>
      <mat-card-title>{{ getTaskTypeDisplay() }} - Testing Mode</mat-card-title>
      <mat-card-subtitle>{{ task.description || 'No description' }}</mat-card-subtitle>
    </mat-card-header>
  </mat-card>
  }

  <mat-dialog-content>
    <!-- Task Summary Card -->
    <mat-card class="mb-20">
      <mat-card-content>
        <div class="g-just-flex gap-20 flex-wrap">
          <div class="task-info-item">
            <div class="label subtitle-text-color-2 small-subtitle">Status</div>
            <div [appChips]="task.status || 'requested'" [fontSize]="'13px'"></div>
          </div>

          @if(task.priority) {
          <div class="task-info-item">
            <div class="label subtitle-text-color-2 small-subtitle">Priority</div>
            <div>{{ task.priority | titlecase }}</div>
          </div>
          }

          @if(task.restriction?.period?.start) {
          <div class="task-info-item">
            <div class="label subtitle-text-color-2 small-subtitle">Due</div>
            <div>{{ task.restriction?.period?.start | date:'short' }}</div>
          </div>
          }

          @if(task.for?.reference) {
          <div class="task-info-item">
            <div class="label subtitle-text-color-2 small-subtitle">Patient</div>
            <div [appReferenceDisplay]="task.for?.reference" [instantFetchAndReplace]="true"></div>
          </div>
          }
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Related Resource Info -->
    @if(relatedServiceRequest || relatedMedicationRequest) {
    <mat-card class="mb-20">
      <mat-card-header>
        <mat-card-title>Linked Resource</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        @if(relatedServiceRequest) {
        <div class="g-just-flex gap-12 align-items-center">
          <mat-icon>assignment</mat-icon>
          <div>
            <div>{{ relatedServiceRequest.code?.text || 'Service Request' }}</div>
            <div class="subtitle-text-color-2 small-subtitle">
              Status: {{ relatedServiceRequest.status | titlecase }}
            </div>
          </div>
        </div>
        }

        @if(relatedMedicationRequest) {
        <div class="g-just-flex gap-12 align-items-center">
          <mat-icon>medication</mat-icon>
          <div>
            <div>{{ relatedMedicationRequest.medicationCodeableConcept?.text || 'Medication' }}</div>
            <div class="subtitle-text-color-2 small-subtitle">
              Status: {{ relatedMedicationRequest.status | titlecase }}
            </div>
          </div>
        </div>
        }
      </mat-card-content>
    </mat-card>
    }

    <!-- Purpose: Fulfill -->
    @if(purpose === 'fulfill') {
    <div class="fulfillment-section">
      <h3 class="section-title">Fulfill Task</h3>
      <p class="subtitle-text-color-2 mb-20">
        Click the button below to open the appropriate form for fulfilling this task.
      </p>

      <button mat-raised-button color="primary" (click)="openFulfillmentForm()" [disabled]="loading">
        <mat-icon>{{ getTaskTypeIcon() }}</mat-icon>
        <span>Open {{ getTaskTypeDisplay() }} Form</span>
      </button>

      @if(completionEvidenceMessages.length > 0) {
      <div class="mt-20">
        @for(message of completionEvidenceMessages; track message) {
        <div class="subtitle-text-color-2 mb-8">{{ message }}</div>
        }
      </div>
      }

      @if(canMarkComplete && showCompletionStep) {
      <div class="mt-20">
        <mat-divider class="my-20"></mat-divider>
        <p class="subtitle-text-color-2 mb-15">Task work has been recorded. You can now mark this task as complete.</p>
        <button mat-raised-button color="accent" (click)="markComplete()" [disabled]="loading">
          <mat-icon>check_circle</mat-icon>
          <span>Mark Task as Complete</span>
        </button>
      </div>
      }
    </div>
    }

    <!-- Purpose: Complete -->
    @if(purpose === 'complete') {
    <div class="completion-section">
      <h3 class="section-title">Complete Task</h3>

      <form [formGroup]="completionForm">
        <!-- Completed Service Requests Selection -->
        @if(completedServiceRequests.length > 0) {
        <div class="mb-20">
          <label class="form-label">Select Completed Service Requests (Optional)</label>
          <p class="subtitle-text-color-2 small-subtitle mb-12">
            Choose which service requests are related to completing this task.
          </p>

          @for(sr of completedServiceRequests; track sr.id) {
          <mat-checkbox [value]="'ServiceRequest/' + sr.id" class="mb-8">
            {{ sr.code?.text || sr.code?.coding?.[0]?.display || 'Service Request ' + sr.id }}
            <span class="subtitle-text-color-2 small-subtitle ml-8">
              ({{ sr.authoredOn | date:'short' }})
            </span>
          </mat-checkbox>
          <br>
          }
        </div>
        }

        <!-- Completed Medication Requests Selection -->
        @if(completedMedicationRequests.length > 0) {
        <div class="mb-20">
          <label class="form-label">Select Completed Medications (Optional)</label>
          <p class="subtitle-text-color-2 small-subtitle mb-12">
            Choose which medications are related to completing this task.
          </p>

          @for(mr of completedMedicationRequests; track mr.id) {
          <mat-checkbox [value]="'MedicationRequest/' + mr.id" class="mb-8">
            {{ mr.medicationCodeableConcept?.text || 'Medication ' + mr.id }}
            <span class="subtitle-text-color-2 small-subtitle ml-8">
              ({{ mr.authoredOn | date:'short' }})
            </span>
          </mat-checkbox>
          <br>
          }
        </div>
        }

        <!-- Completion Notes -->
        <mat-form-field appearance="outline" class="w-100 mb-20">
          <mat-label>Completion Notes (Optional)</mat-label>
          <textarea matInput formControlName="completionNotes" rows="4"
            placeholder="Add any notes about task completion..."></textarea>
        </mat-form-field>

        <!-- Outcome Selection -->
        <div class="mb-20">
          <label class="form-label">Task Outcome</label>
          <mat-radio-group formControlName="outcome">
            <mat-radio-button value="completed" class="mr-20">Completed</mat-radio-button>
            <mat-radio-button value="failed">Failed</mat-radio-button>
          </mat-radio-group>
        </div>
      </form>

      @if(!canMarkComplete) {
      <div class="warning-box mb-20">
        <mat-icon>warning</mat-icon>
        <div>
          <strong>No completed work found</strong>
          @if(completionValidationMessages.length > 0) {
          @for(message of completionValidationMessages; track message) {
          <p class="small-subtitle mb-0">{{ message }}</p>
          }
          } @else {
          <p class="small-subtitle mb-0">
            You need to fulfill the task requirements (record vitals, complete service requests, etc.) before marking
            this task as complete.
          </p>
          }
          @if(completionEvidenceMessages.length > 0) {
          @for(message of completionEvidenceMessages; track message) {
          <p class="small-subtitle mb-0">{{ message }}</p>
          }
          }
        </div>
      </div>
      }
    </div>
    }

    <!-- Purpose: Review -->
    @if(purpose === 'review') {
    <div class="review-section">
      <h3 class="section-title">Task Review</h3>
      <p class="subtitle-text-color-2">Review task details and history.</p>

      <!-- Task Inputs -->
      @if(task.input && task.input.length > 0) {
      <div class="mb-20">
        <h4>Task Inputs</h4>
        @for(input of task.input; track $index) {
        <div class="info-item mb-12">
          <div class="label subtitle-text-color-2 small-subtitle">{{ input.type?.text || 'Input' }}</div>
          <div>{{ input.valueString || input.valueReference?.reference || 'N/A' }}</div>
        </div>
        }
      </div>
      }

      <!-- Task Outputs -->
      @if(task.output && task.output.length > 0) {
      <div class="mb-20">
        <h4>Task Outputs</h4>
        @for(output of task.output; track $index) {
        <div class="info-item mb-12">
          <div class="label subtitle-text-color-2 small-subtitle">{{ output.type?.text || 'Output' }}</div>
          <div>{{ output.valueString || output.valueReference?.reference || 'N/A' }}</div>
        </div>
        }
      </div>
      }
    </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button mat-button (click)="close()">Cancel</button>

    @if(purpose === 'fulfill' && task.status === 'requested') {
    <button mat-raised-button (click)="markInProgress()" [disabled]="loading">
      <mat-icon>play_arrow</mat-icon>
      Start Task
    </button>
    }

    @if(purpose === 'complete' && canMarkComplete) {
    <button mat-raised-button color="primary" (click)="markComplete()" [disabled]="loading">
      <mat-icon>check_circle</mat-icon>
      Mark Complete
    </button>
    }
  </mat-dialog-actions>
</div>